import {
  ASSISTANT_COLORS,
  type AssistantTask,
  type CrowdexAssistant,
  type TaskAutomation,
  type UpdateAssistantInput,
} from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Textarea } from '@crowdex/ui/components/textarea';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, ChevronRight, Cloud, Palette, Pencil, Plus, SquareUserRound } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import type { AssistantMemberView } from './assistant-conversation-api';
import {
  agentLabel,
  assistantColorName,
  automationName,
  plannedState,
  taskIconState,
  taskStepLine,
  tasksDoneLabel,
} from './assistant-model';
import { PersonaAvatar, TaskStateIcon, WorkflowGlyph } from './assistant-parts';
import { assistantAutomationList } from './use-assistant';

/** An avatar the call plugin no longer lists: the assistant speaks with the default voice. */
const AVATAR_GONE = 'Unavailable · default voice';

/** The avatar choice that is none: the default voice. */
const NO_AVATAR = 'none';

/** The default automation that is none: its tasks are done by an agent alone. */
const AGENT_ALONE = 'agent-alone';

/** A repository of the assistant, by its id and its name. */
export type AssistantRepositoryView = { id: string; name: string };

/** A titled card of rows, as the mobile settings and D67 draw them. */
function Rows({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1.5" aria-label={title}>
      <div className="flex items-baseline justify-between px-1">
        <h3 className="type-meta text-muted-foreground">{title}</h3>
        {aside}
      </div>
      <div className="divide-y divide-hairline overflow-hidden rounded-card border bg-card">
        {children}
      </div>
    </section>
  );
}

function Row({
  icon,
  label,
  detail,
  onClick,
  children,
  className,
  chevron = true,
}: {
  chevron?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  detail?: ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
}) {
  const content = (
    <>
      {icon}
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      {detail !== undefined && (
        <span className="type-meta shrink-0 truncate text-muted-foreground">{detail}</span>
      )}
      {children}
      {onClick !== undefined && chevron && (
        <ChevronRight aria-hidden className="size-4 shrink-0 text-subtle-foreground" />
      )}
    </>
  );
  const classes = cn('flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left', className);
  return onClick === undefined ? (
    <div className={classes}>{content}</div>
  ) : (
    <button className={cn(classes, 'hover:bg-accent/40')} onClick={onClick} type="button">
      {content}
    </button>
  );
}

/** An avatar an assistant may speak as, and the assistant that has it now, if any. */
export type AssistantAvatarView = { id: string; name: string; usedBy: string | null };

/**
 * Which of the user's automations an assistant may run once `id` is switched: those it names
 * (empty: all), never none.
 */
export function toggledAutomationIds(
  listed: readonly Pick<TaskAutomation, 'id'>[],
  current: readonly string[],
  id: string,
): string[] {
  const all = listed.map((automation) => automation.id);
  const chosen = current.length === 0 ? all : all.filter((entry) => current.includes(entry));
  const next = chosen.includes(id) ? chosen.filter((entry) => entry !== id) : [...chosen, id];
  if (next.length === 0) return current.length === 0 ? [] : [...current];
  return next.length === all.length ? [] : all.filter((entry) => next.includes(entry));
}

/**
 * The assistant panel's settings (D67): cards of rows — Agents (the saved agents it hands work
 * to), Automations (those of the user's it may run, and its default: none, an agent alone), the
 * assistant's own settings (avatar, instructions, repositories, colour) — and "Archive the
 * assistant", which the
 * home assistant has not, as it has no repositories. As a page (mobile 2e) it starts with its
 * name, its two buttons and its Tasks.
 */
export function AssistantSettingsTab({
  project,
  automations: listed = [],
  avatars = [],
  avatarsKnown = false,
  personas,
  repositories,
  tasks,
  variant = 'tab',
  onUpdate,
  onOpenAutomation,
  onOpenTask,
  onConversation,
  onNewTask,
  savedAgents = [],
  agents = [],
}: {
  project: CrowdexAssistant;
  /** The user's automations a task may run, among which the assistant runs those it names. */
  automations?: readonly TaskAutomation[];
  /** The avatars it may speak as (the call plugin's personas). */
  avatars?: readonly AssistantAvatarView[];
  /** `avatars` is the call plugin's answer: an avatar it does not list is gone. */
  avatarsKnown?: boolean;
  personas: readonly AssistantMemberView[];
  repositories: readonly AssistantRepositoryView[];
  tasks: readonly AssistantTask[] | null;
  variant?: 'tab' | 'page';
  onUpdate: (input: UpdateAssistantInput) => Promise<void>;
  /** Opens an automation (none: the list) in the app that edits it. */
  onOpenAutomation?: (automation?: TaskAutomation) => void;
  onOpenTask: (conversationId: string) => void;
  onConversation?: () => void;
  onNewTask?: () => void;
  /** The user's saved agents, those the assistant may hand work to. */
  savedAgents?: readonly { id: string; name: string }[];
  /** The agents of this machine (names of the personas' agents). */
  agents?: readonly { id: string; name: string }[];
}) {
  const [instructions, setInstructions] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const update = (input: UpdateAssistantInput) => {
    setError(null);
    void onUpdate(input).catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : String(reason)),
    );
  };
  const editable = project.canEdit !== false;
  const home = project.isHome === true;
  // What its agent starts tasks with: those of the user's automations it names, else all.
  const runs = assistantAutomationList(listed, project);
  const automationIds = project.automationIds ?? [];
  const avatar = avatars.find((entry) => entry.id === project.avatarId) ?? null;
  // Its avatar the call plugin no longer lists: calls speak with the default voice.
  const avatarGone = avatarsKnown && project.avatarId != null && avatar === null;
  const repositoryName = (id: string) =>
    repositories.find((repository) => repository.id === id)?.name ?? 'the repository';
  const lines = project.instructions.split('\n').filter((line) => line.trim()).length;
  const done = (tasks ?? []).filter((task) => task.workflowRun.state === 'succeeded').length;
  const canAddPersona =
    editable && savedAgents.some((agent) => !project.memberAgentIds.includes(agent.id));

  return (
    <div
      className={cn('flex flex-col gap-5 px-4 py-3', variant === 'page' && 'pb-8')}
      data-project-settings={variant}
    >
      {variant === 'page' ? (
        <header className="flex flex-col gap-3">
          <h2 className="type-page-title flex items-center gap-2.5">
            <span
              aria-hidden
              className="size-3.5 shrink-0 rounded-[4px]"
              style={{ backgroundColor: project.color }}
            />
            {project.name}
          </h2>
          {project.goal !== '' && <p className="type-meta text-muted-foreground">{project.goal}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onConversation} size="lg" variant="outline">
              Conversation
            </Button>
            <Button onClick={onNewTask} size="lg">
              New task
            </Button>
          </div>
        </header>
      ) : (
        <header className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-xl font-semibold text-foreground"
            style={{ backgroundColor: project.color }}
          >
            {project.name.trim()[0]?.toUpperCase()}
          </span>
          <span className="min-w-0">
            <span className="type-card-title block truncate">{project.name}</span>
            {project.goal && (
              <span className="type-meta block truncate text-muted-foreground">{project.goal}</span>
            )}
          </span>
        </header>
      )}

      {variant === 'page' && tasks !== null && tasks.length > 0 && (
        <Rows
          aside={
            <span className="type-meta text-muted-foreground">
              {tasksDoneLabel(done, tasks.length)}
            </span>
          }
          title="Tasks"
        >
          {tasks.map((task) => (
            <Row
              icon={<TaskStateIcon state={taskIconState(task)} />}
              key={task.conversationId}
              label={
                <span className="block min-w-0">
                  <span className="block truncate">{task.title}</span>
                  <span
                    className={cn(
                      'type-meta block truncate',
                      task.workflowRun.state === 'waiting'
                        ? 'text-attention'
                        : 'text-muted-foreground',
                    )}
                  >
                    {[
                      automationName(task.workflowRun, listed),
                      taskStepLine(task, plannedState(task.conversationId, tasks)),
                    ]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  </span>
                </span>
              }
              onClick={() => onOpenTask(task.conversationId)}
            />
          ))}
        </Rows>
      )}

      <Rows title="Agents">
        {personas.map((persona) =>
          !editable ? (
            <Row
              detail={agentLabel(persona.agent, agents)}
              icon={<PersonaAvatar persona={persona} personas={personas} size="sm" />}
              key={persona.agentId}
              label={persona.name}
            />
          ) : (
            <DropdownMenu key={persona.agentId}>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={`${persona.name}, on ${agentLabel(persona.agent, agents)}`}
                  className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/40 data-[state=open]:bg-accent/40"
                  type="button"
                >
                  <PersonaAvatar persona={persona} personas={personas} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm">{persona.name}</span>
                  <span className="type-meta shrink-0 text-muted-foreground">
                    {agentLabel(persona.agent, agents)}
                  </span>
                  <ChevronRight aria-hidden className="size-4 shrink-0 text-subtle-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() =>
                    update({
                      memberAgentIds: project.memberAgentIds.filter((id) => id !== persona.agentId),
                    })
                  }
                  variant="destructive"
                >
                  Remove from the assistant
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        )}
        {personas.length === 0 && !canAddPersona && (
          <p className="type-meta px-3 py-2 text-muted-foreground">No agent yet.</p>
        )}
        {canAddPersona && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left text-muted-foreground text-sm hover:bg-accent/40"
                type="button"
              >
                <Plus aria-hidden className="size-4" />
                Add an agent
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {savedAgents
                .filter((agent) => !project.memberAgentIds.includes(agent.id))
                .map((agent) => (
                  <DropdownMenuItem
                    key={agent.id}
                    onSelect={() =>
                      update({ memberAgentIds: [...project.memberAgentIds, agent.id] })
                    }
                  >
                    {agent.name}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Rows>

      {listed.length > 0 && (
        <Rows
          aside={
            onOpenAutomation === undefined ? undefined : (
              <button
                className="type-meta text-muted-foreground hover:text-foreground"
                onClick={() => onOpenAutomation()}
                type="button"
              >
                Open
              </button>
            )
          }
          title="Automations"
        >
          {listed.map((automation) => {
            const runsIt = runs.automations.some((entry) => entry.id === automation.id);
            return (
              <Row
                icon={<WorkflowGlyph />}
                key={automation.id}
                label={automation.name}
                {...(editable
                  ? {
                      onClick: () => {
                        const next = toggledAutomationIds(listed, automationIds, automation.id);
                        // Its default leaves with it: its tasks then go to an agent alone.
                        const keepsDefault =
                          next.length === 0 || next.includes(project.defaultAutomation ?? '');
                        update({
                          automationIds: next,
                          ...(keepsDefault ? {} : { defaultAutomation: null }),
                        });
                      },
                    }
                  : {})}
                chevron={false}
              >
                {runsIt ? (
                  <Check aria-label="Runs it" className="size-4 shrink-0" role="img" />
                ) : (
                  <span aria-hidden className="size-4 shrink-0" />
                )}
              </Row>
            );
          })}
          <Row label="Default">
            {!editable ? (
              <span className="type-meta text-muted-foreground">
                {runs.default?.name ?? 'Agent alone'}
              </span>
            ) : (
              <Select
                onValueChange={(value) =>
                  update({ defaultAutomation: value === AGENT_ALONE ? null : value })
                }
                value={runs.default?.id ?? AGENT_ALONE}
              >
                <SelectTrigger
                  aria-label="Default automation"
                  className="h-control-compact w-auto border-0 bg-transparent text-muted-foreground shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AGENT_ALONE}>Agent alone</SelectItem>
                  {runs.automations.map((automation) => (
                    <SelectItem key={automation.id} value={automation.id}>
                      {automation.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Row>
        </Rows>
      )}

      <Rows title="Assistant">
        <Row
          icon={<SquareUserRound aria-hidden className="size-4 text-muted-foreground" />}
          label="Avatar"
        >
          {!editable ? (
            <span className="type-meta text-muted-foreground">
              {avatar?.name ?? (avatarGone ? AVATAR_GONE : 'None')}
            </span>
          ) : (
            <Select
              onValueChange={(value) => update({ avatarId: value === NO_AVATAR ? null : value })}
              value={project.avatarId ?? NO_AVATAR}
            >
              <SelectTrigger
                aria-label="Avatar"
                className="h-control-compact w-auto border-0 bg-transparent text-muted-foreground shadow-none"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_AVATAR}>None</SelectItem>
                {avatarGone && project.avatarId != null && (
                  <SelectItem value={project.avatarId}>{AVATAR_GONE}</SelectItem>
                )}
                {/* One of another assistant's moves here: an avatar serves one assistant. */}
                {avatars.map((entry) => (
                  <SelectItem key={entry.id} value={entry.id}>
                    {entry.usedBy === null || entry.id === project.avatarId
                      ? entry.name
                      : `${entry.name} · ${entry.usedBy}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Row>
        {instructions === null ? (
          <Row
            detail={lines === 0 ? 'none' : `${lines} line${lines === 1 ? '' : 's'}`}
            icon={<Pencil aria-hidden className="size-4 text-muted-foreground" />}
            label="Instructions"
            onClick={editable ? () => setInstructions(project.instructions) : undefined}
          />
        ) : (
          <form
            className="flex flex-col gap-2 px-3 py-2"
            onSubmit={(event) => {
              event.preventDefault();
              update({ instructions });
              setInstructions(null);
            }}
          >
            <Textarea
              aria-label="Instructions"
              autoFocus
              onChange={(event) => setInstructions(event.target.value)}
              placeholder="Small PRs. Navigation and database changes go to Careful."
              rows={4}
              value={instructions}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setInstructions(null)} size="sm" type="button" variant="ghost">
                Cancel
              </Button>
              <Button size="sm" type="submit" variant="secondary">
                Save
              </Button>
            </div>
          </form>
        )}
        {(home ? [] : project.repositoryIds).map((id) => (
          <Row
            icon={<Cloud aria-hidden className="size-4 text-muted-foreground" />}
            key={id}
            label={repositoryName(id)}
          />
        ))}
        <Row icon={<Palette aria-hidden className="size-4 text-muted-foreground" />} label="Colour">
          {!editable ? (
            <span
              aria-label={`Colour: ${assistantColorName(project.color)}`}
              className="size-4 rounded-[4px]"
              role="img"
              style={{ backgroundColor: project.color }}
            />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={`Colour: ${assistantColorName(project.color)}`}
                  className="size-4 rounded-[4px]"
                  style={{ backgroundColor: project.color }}
                  type="button"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="flex min-w-0 gap-1 p-2">
                {ASSISTANT_COLORS.map((color) => (
                  <DropdownMenuItem
                    aria-label={assistantColorName(color)}
                    className="size-7 min-h-0 justify-center p-0"
                    key={color}
                    onSelect={() => update({ color })}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'size-4 rounded-[4px]',
                        color === project.color &&
                          'ring-2 ring-foreground ring-offset-1 ring-offset-popover',
                      )}
                      style={{ backgroundColor: color }}
                    />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </Row>
      </Rows>

      {error !== null && (
        <p className="type-meta text-destructive" role="alert">
          {error}
        </p>
      )}
      {editable && !home && (
        <button
          className="self-start px-1 text-destructive text-sm hover:underline"
          onClick={() => update({ archived: true })}
          type="button"
        >
          Archive the assistant
        </button>
      )}
    </div>
  );
}
